import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import GalleryImagePicker from './GalleryImagePicker';
import {
  createEmptyItem,
  SUGGESTED_ATTRIBUTE_LABELS
} from './constants';


const GalleryItemDialog = ({open, onClose, onSave, item, surgeons}) => {
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(item ? JSON.parse(JSON.stringify(item)) : createEmptyItem());
    }
  }, [open, item]);

  if (!draft) {
    return null;
  }

  const setDraftField = (field, value) => {
    setDraft((prevDraft) => ({...prevDraft, [field]: value}));
  };

  const setAttributeField = (index, field, value) => {
    setDraft((prevDraft) => ({
      ...prevDraft,
      attributes: prevDraft.attributes.map((attribute, attributeIndex) => (
        attributeIndex === index ? {...attribute, [field]: value} : attribute
      ))
    }));
  };

  const addAttribute = (label = '') => {
    setDraft((prevDraft) => ({
      ...prevDraft,
      attributes: [...prevDraft.attributes, {label, value: ''}]
    }));
  };

  const removeAttribute = (index) => {
    setDraft((prevDraft) => ({
      ...prevDraft,
      attributes: prevDraft.attributes.filter((attribute, attributeIndex) => attributeIndex !== index)
    }));
  };

  const removeImage = (index) => {
    setDraft((prevDraft) => ({
      ...prevDraft,
      images: prevDraft.images.filter((image, imageIndex) => imageIndex !== index)
    }));
  };

  const hasAttributeLabel = (label) => draft.attributes.some(
    (attribute) => attribute.label.trim().toLowerCase() === label.toLowerCase()
  );

  const enabledSurgeons = surgeons.filter((surgeon) => surgeon.enabled == 1);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{item ? 'Edit Gallery Item' : 'Add Gallery Item'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3} className="mb-4">
          <Grid item xs={12} sm={2} className="my-2">Enable</Grid>
          <Grid item xs={12} sm={9}>
            <Switch
              color="primary"
              checked={draft.enabled == 1}
              onChange={(event) => setDraftField('enabled', event.target.checked ? 1 : 0)}
            />
          </Grid>
          <Grid item xs={12} sm={2} className="my-2">Title</Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              className="w-full max-w-450"
              variant="outlined"
              size="small"
              placeholder="e.g. Patient 1"
              value={draft.title}
              onChange={(event) => setDraftField('title', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2} className="my-2">Description</Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              className="w-full"
              variant="outlined"
              size="small"
              multiline
              minRows={2}
              value={draft.description}
              onChange={(event) => setDraftField('description', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2} className="my-2">Sort Order</Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              className="max-w-90"
              variant="outlined"
              size="small"
              type="number"
              value={draft.sort_order}
              onChange={(event) => setDraftField('sort_order', event.target.value)}
            />
          </Grid>
        </Grid>
        <Divider className="my-4" />
        <Typography variant="subtitle1" className="mb-2">
          Images ({draft.images.length})
        </Typography>
        <div className="flex flex-wrap mb-2">
          {draft.images.map((image, index) => (
            <div key={`item-image-${index}`} className="flex items-start mr-4 mb-2">
              <img src={image} alt={`Item ${index + 1}`} className="max-h-100 max-w-150" />
              <IconButton
                size="small"
                title="Remove image"
                onClick={() => removeImage(index)}
              >
                <Icon>delete</Icon>
              </IconButton>
            </div>
          ))}
        </div>
        {!draft.images.length &&
          <FormHelperText error className="mb-2">
            At least one image is required. Items without images are not displayed on the gallery page.
          </FormHelperText>
        }
        <GalleryImagePicker
          id="before-after-gallery-item-image"
          dialogTitle="Select Item Image"
          allowPng={false}
          recommendedSize="300x600px"
          rowHeight={120}
          onSelect={(imageUrl) => setDraftField('images', [...draft.images, imageUrl])}
        />
        <Divider className="my-4" />
        <Grid container spacing={3} className="mb-4">
          <Grid item xs={12} sm={2} className="my-2">Tags</Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              className="w-full max-w-450"
              variant="outlined"
              size="small"
              placeholder="e.g. facelift, female"
              value={draft.tags}
              onChange={(event) => setDraftField('tags', event.target.value)}
            />
            <FormHelperText>
              Comma-separated tags used for filtering images
            </FormHelperText>
          </Grid>
          <Grid item xs={12} sm={2} className="my-2">Surgeons</Grid>
          <Grid item xs={12} sm={9}>
            {enabledSurgeons.length ?
              <FormControl variant="outlined" size="small" className="w-full max-w-450">
                <InputLabel id="gallery-item-surgeons-label">Surgeons</InputLabel>
                <Select
                  labelId="gallery-item-surgeons-label"
                  label="Surgeons"
                  multiple
                  value={draft.surgeons}
                  onChange={(event) => setDraftField('surgeons', event.target.value)}
                  renderValue={(selected: number[]) => enabledSurgeons
                    .filter((surgeon) => selected.includes(surgeon.id))
                    .map((surgeon) => surgeon.name)
                    .join(', ')}
                >
                  {enabledSurgeons.map((surgeon) => (
                    <MenuItem key={`surgeon-option-${surgeon.id}`} value={surgeon.id}>
                      <Checkbox color="primary" checked={draft.surgeons.includes(surgeon.id)} />
                      <ListItemText primary={surgeon.name || `Surgeon #${surgeon.id}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              :
              <FormHelperText className="my-2">
                No surgeons configured yet. Add them on the Surgeons tab first.
              </FormHelperText>
            }
            <FormHelperText>
              The &quot;Surgeon&quot; attribute is added to the item automatically based on this selection
            </FormHelperText>
          </Grid>
        </Grid>
        <Divider className="my-4" />
        <Typography variant="subtitle1" className="mb-2">Attributes</Typography>
        <FormHelperText className="mb-2">
          Label + value pairs displayed next to the item, e.g. procedures performed, months post-op, patient age
        </FormHelperText>
        {draft.attributes.map((attribute, index) => (
          <Grid container spacing={2} className="items-center mb-2" key={`attribute-${index}`}>
            <Grid item xs={12} sm={5}>
              <TextField
                className="w-full"
                variant="outlined"
                size="small"
                label="Label"
                value={attribute.label}
                disabled={SUGGESTED_ATTRIBUTE_LABELS.includes(attribute.label)}
                onChange={(event) => setAttributeField(index, 'label', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                className="w-full"
                variant="outlined"
                size="small"
                label="Value"
                value={attribute.value}
                onChange={(event) => setAttributeField(index, 'value', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton
                size="small"
                title="Delete attribute"
                onClick={() => removeAttribute(index)}
              >
                <Icon>delete</Icon>
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <div className="flex flex-wrap items-center mt-2">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            className="mr-4 mb-2"
            onClick={() => addAttribute()}
          >
            <Icon className="mr-2">add</Icon>
            Add Attribute
          </Button>
          {SUGGESTED_ATTRIBUTE_LABELS.map((label) => (
            <Chip
              key={`suggested-${label}`}
              className="mr-2 mb-2"
              label={label}
              size="small"
              disabled={hasAttributeLabel(label)}
              onClick={() => addAttribute(label)}
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="contained">
          Cancel
        </Button>
        <Button
          onClick={() => onSave(draft)}
          color="primary"
          variant="contained"
          disabled={!draft.images.length}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GalleryItemDialog;
